import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { 
  CheckCircle, Lock, Eye, EyeOff, AlertCircle, 
  Calendar, User, Shield, ArrowRight, PartyPopper, Car
} from 'lucide-react';
import { toast } from 'sonner';

/**
 * GuestBookingSuccessPage - Clean success + first-time password setup for guest checkout users.
 * 
 * Flow:
 * 1. Shows booking success confirmation
 * 2. Immediate in-app password setup (first-time, NO current password required)
 * 3. After password set -> redirect to dashboard
 * 
 * This replaces the confusing redirect to /set-password which asked for "current password"
 */
const GuestBookingSuccessPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, api, updateUser } = useAuth();
  
  // Get booking info from location state
  const bookingId = location.state?.bookingId;
  const vehicleTitle = location.state?.vehicleTitle;
  const vehicleId = location.state?.vehicleId;
  const startDate = location.state?.startDate;
  const endDate = location.state?.endDate;
  
  const [step, setStep] = useState('success'); // 'success' | 'password' | 'complete'
  const [formData, setFormData] = useState({
    password: '',
    confirm_password: ''
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Redirect if not a guest checkout user
  useEffect(() => {
    if (!user) {
      navigate('/login', { replace: true });
      return;
    }
    
    // If user already has password set (not required), go to dashboard
    if (user.password_reset_required === false) {
      navigate('/dashboard', { replace: true });
    }
  }, [user, navigate]);

  const validatePassword = () => {
    const newErrors = {};
    
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    }
    
    if (!formData.confirm_password) {
      newErrors.confirm_password = 'Please confirm your password';
    } else if (formData.password !== formData.confirm_password) {
      newErrors.confirm_password = 'Passwords do not match';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmitPassword = async (e) => {
    e.preventDefault();
    
    if (!validatePassword()) return;
    
    setLoading(true);
    try {
      // Use the guest-activate endpoint (first-time password, no current password required)
      const response = await api.post('/auth/guest-activate', {
        password: formData.password,
        confirm_password: formData.confirm_password
      });
      
      if (response.data.success) {
        // Update token and user data
        localStorage.setItem('token', response.data.token);
        updateUser(response.data.user);
        
        toast.success('Account activated! Welcome to HireHub.');
        setStep('complete');
        
        // Redirect to dashboard after short delay
        setTimeout(() => {
          navigate('/dashboard', { replace: true });
        }, 2000);
      }
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to set password';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  // Password strength indicator
  const getPasswordStrength = (password) => {
    if (!password) return { strength: 0, label: '', color: '' };
    
    let strength = 0;
    if (password.length >= 8) strength++;
    if (password.length >= 12) strength++;
    if (/[A-Z]/.test(password)) strength++;
    if (/[a-z]/.test(password)) strength++;
    if (/[0-9]/.test(password)) strength++;
    if (/[^A-Za-z0-9]/.test(password)) strength++;

    if (strength <= 2) return { strength: 33, label: 'Weak', color: 'bg-red-500' };
    if (strength <= 4) return { strength: 66, label: 'Medium', color: 'bg-yellow-500' };
    return { strength: 100, label: 'Strong', color: 'bg-green-500' };
  };

  const passwordStrength = getPasswordStrength(formData.password);

  // Skip password for now - use email link later
  const handleSkip = () => {
    toast.info('You can set your password anytime using the link sent to your email.');
    navigate('/dashboard', { replace: true });
  };

  // Return to booking - restore dates in sessionStorage so VehicleDetails can use them
  const handleReturnToBooking = () => {
    if (vehicleId && startDate && endDate) {
      // Restore dates to sessionStorage for VehicleDetails to pick up
      sessionStorage.setItem('bookingStartDate', startDate);
      sessionStorage.setItem('bookingEndDate', endDate);
      sessionStorage.setItem('bookingVehicleId', vehicleId);
      navigate(`/vehicles/${vehicleId}`);
    } else if (bookingId) {
      // Fallback: go to booking details
      navigate(`/bookings/${bookingId}`);
    } else {
      navigate('/dashboard');
    }
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
      <Card className="w-full max-w-md bg-white/10 backdrop-blur-lg border-white/20 text-white">
        
        {/* Step 1: Success Message */}
        {step === 'success' && (
          <>
            <CardHeader className="text-center pb-4">
              <div className="mx-auto w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mb-4">
                <CheckCircle className="w-10 h-10 text-green-400" />
              </div>
              <CardTitle className="text-2xl font-bold text-green-400">
                Booking Request Sent!
              </CardTitle>
            </CardHeader>
            
            <CardContent className="space-y-6">
              {/* Booking Summary */}
              <div className="p-4 bg-white/5 rounded-xl space-y-3">
                {vehicleTitle && (
                  <div className="flex items-center gap-3">
                    <Calendar className="w-5 h-5 text-purple-400" />
                    <span className="text-sm text-white/80">{vehicleTitle}</span>
                  </div>
                )}
                <div className="flex items-center gap-3">
                  <User className="w-5 h-5 text-blue-400" />
                  <span className="text-sm text-white/80">{user?.full_name}</span>
                </div>
                {bookingId && (
                  <div className="text-xs text-white/50 font-mono">
                    Ref: {bookingId}
                  </div>
                )}
              </div>

              {/* What happens next */}
              <div className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-xl">
                <p className="text-sm text-blue-300 font-medium mb-2">What happens next?</p>
                <ul className="text-xs text-white/70 space-y-1">
                  <li>• The host will review and respond to your request</li>
                  <li>• You'll receive a notification when they respond</li>
                  <li>• No payment is taken until the host accepts</li>
                </ul>
              </div>

              {/* CTA to set password */}
              <div className="pt-2 space-y-3">
                <p className="text-center text-sm text-white/60 mb-4">
                  One last step to secure your account:
                </p>
                <Button
                  onClick={() => setStep('password')}
                  className="w-full bg-primary hover:bg-primary/90 h-12 text-base"
                  data-testid="continue-to-password-btn"
                >
                  Set Your Password
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
                
                {/* Return to booking button - restores dates */}
                {vehicleId && (
                  <Button
                    onClick={handleReturnToBooking}
                    variant="outline"
                    className="w-full border-white/20 text-white hover:bg-white/10 h-11"
                    data-testid="return-to-booking-btn"
                  >
                    <Car className="w-4 h-4 mr-2" />
                    Return to your booking
                  </Button>
                )}
                
                <button
                  onClick={handleSkip}
                  className="w-full text-sm text-white/50 hover:text-white/70 transition-colors"
                >
                  I'll do this later via email
                </button>
              </div>
            </CardContent>
          </>
        )}

        {/* Step 2: Password Setup */}
        {step === 'password' && (
          <>
            <CardHeader className="text-center pb-2">
              <div className="mx-auto w-16 h-16 bg-purple-500/20 rounded-full flex items-center justify-center mb-4">
                <Shield className="w-8 h-8 text-purple-400" />
              </div>
              <CardTitle className="text-xl font-bold">Create Your Password</CardTitle>
              <p className="text-sm text-white/60 mt-2">
                Secure your HireHub account
              </p>
            </CardHeader>
            
            <CardContent>
              <form onSubmit={handleSubmitPassword} className="space-y-4">
                {/* Password */}
                <div className="space-y-2">
                  <Label htmlFor="password" className="text-slate-200">
                    Password
                  </Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="password"
                      name="password"
                      type={showPassword ? 'text' : 'password'}
                      value={formData.password}
                      onChange={handleChange}
                      className={`pl-10 pr-10 bg-white/5 border-white/20 text-white placeholder:text-slate-400 h-12 ${
                        errors.password ? 'border-red-500' : ''
                      }`}
                      placeholder="Create a secure password"
                      data-testid="guest-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  
                  {/* Password strength */}
                  {formData.password && (
                    <div className="space-y-1">
                      <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
                        <div 
                          className={`h-full transition-all duration-300 ${passwordStrength.color}`}
                          style={{ width: `${passwordStrength.strength}%` }}
                        />
                      </div>
                      <p className={`text-xs ${
                        passwordStrength.label === 'Weak' ? 'text-red-400' :
                        passwordStrength.label === 'Medium' ? 'text-yellow-400' : 'text-green-400'
                      }`}>
                        {passwordStrength.label}
                      </p>
                    </div>
                  )}
                  
                  {errors.password && (
                    <p className="text-red-400 text-sm flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" />
                      {errors.password}
                    </p>
                  )}
                </div>

                {/* Confirm Password */}
                <div className="space-y-2">
                  <Label htmlFor="confirm_password" className="text-slate-200">
                    Confirm Password
                  </Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="confirm_password"
                      name="confirm_password"
                      type={showConfirmPassword ? 'text' : 'password'}
                      value={formData.confirm_password}
                      onChange={handleChange}
                      className={`pl-10 pr-10 bg-white/5 border-white/20 text-white placeholder:text-slate-400 h-12 ${
                        errors.confirm_password ? 'border-red-500' : ''
                      }`}
                      placeholder="Confirm your password"
                      data-testid="guest-confirm-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
                    >
                      {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  
                  {errors.confirm_password && (
                    <p className="text-red-400 text-sm flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" />
                      {errors.confirm_password}
                    </p>
                  )}
                  
                  {formData.confirm_password && formData.password === formData.confirm_password && !errors.confirm_password && (
                    <p className="text-green-400 text-sm flex items-center gap-1">
                      <CheckCircle className="w-3 h-3" />
                      Passwords match
                    </p>
                  )}
                </div>

                {/* Requirements hint */}
                <div className="p-3 bg-white/5 rounded-lg text-xs text-white/60">
                  <div className="flex items-center gap-2">
                    {formData.password.length >= 8 ? (
                      <CheckCircle className="w-3 h-3 text-green-400" />
                    ) : (
                      <span className="w-3 h-3 rounded-full border border-white/30" />
                    )}
                    At least 8 characters
                  </div>
                </div>

                <Button
                  type="submit"
                  className="w-full bg-primary hover:bg-primary/90 h-12 text-base"
                  disabled={loading}
                  data-testid="guest-set-password-submit"
                >
                  {loading ? (
                    <span className="flex items-center gap-2">
                      <span className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                      Activating...
                    </span>
                  ) : (
                    'Activate Account'
                  )}
                </Button>
              </form>
              
              <button
                onClick={handleSkip}
                className="w-full mt-4 text-sm text-white/50 hover:text-white/70 transition-colors"
              >
                Skip for now
              </button>
            </CardContent>
          </>
        )}

        {/* Step 3: Complete */}
        {step === 'complete' && (
          <>
            <CardHeader className="text-center pb-4">
              <div className="mx-auto w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mb-4">
                <PartyPopper className="w-10 h-10 text-green-400" />
              </div>
              <CardTitle className="text-2xl font-bold text-green-400">
                You're All Set!
              </CardTitle>
            </CardHeader>
            
            <CardContent className="text-center">
              <p className="text-white/70 mb-4">
                Your account is now active. Redirecting to your dashboard...
              </p>
              <div className="flex justify-center">
                <span className="animate-spin rounded-full h-6 w-6 border-2 border-primary border-t-transparent" />
              </div>
            </CardContent>
          </>
        )}
      </Card>
    </div>
  );
};

export default GuestBookingSuccessPage;
